// Nhận lời mời từ link email (SAA-2026-011). Chưa có phiên ⇒ hai lối « Tạo tài khoản » / « Đăng nhập », cả hai giữ
// đích quay về (URL + localStorage, vì link xác minh email mở ở tab khác). Lỗi ⇒ câu theo nguyên nhân + lời mời đang chờ.
import { qk } from "@secretaa/sdk-react";
import { Button, Card } from "@secretaa/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router";
import { api } from "../../app/api.ts";
import { useSession } from "../../app/session.ts";
import { errorCode, errorMessage } from "../../shared/errors.ts";
import { FormError, Notice } from "../../shared/form.tsx";
import { rememberReturnTo, withNext } from "../../shared/return-to.ts";
import { PendingInvitations } from "./pending-invitations.tsx";

export function AcceptInvitePage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const session = useSession();
  const qc = useQueryClient();
  const [state, setState] = useState<"idle" | "working" | { ws: string } | { error: string; code: string | null }>("idle");
  const started = useRef(false);
  const here = location.pathname + location.search;
  const signup = params.get("signup") === "1";
  const invitedEmail = params.get("email");
  const prefill: Record<string, string> = invitedEmail ? { email: invitedEmail } : {};

  useEffect(() => {
    if (session.isError) rememberReturnTo(here);
  }, [session.isError, here]);

  useEffect(() => {
    if (!session.data || started.current) return;
    started.current = true;
    setState("working");
    void (async () => {
      try {
        await api.csrf();
        const r = await api.invitations.accept(params.get("token") ?? "");
        await qc.invalidateQueries({ queryKey: qk.me() });
        setState({ ws: r.workspaceId });
      } catch (e) {
        setState({ error: errorMessage(t, e), code: errorCode(e) });
      }
    })();
  }, [session.data, params, qc, t]);

  const switchAccount = async () => {
    await api.csrf();
    await api.auth.logout();
    rememberReturnTo(here);
    qc.clear();
    navigate(withNext("/login", here, prefill));
  };

  return (
    <main id="main" className="mx-auto flex min-h-full max-w-md flex-col justify-center p-6">
      <Card>
        <h1 className="mb-4 text-xl font-semibold">{t("invite.title")}</h1>
        {session.isError ? (
          <>
            <Notice>{signup ? (invitedEmail ? t("invite.needAccount", { email: invitedEmail }) : t("invite.needAccountNoEmail")) : t("invite.needLogin")}</Notice>
            {/* Mail cho email chưa có tài khoản (`signup=1`) đưa « Tạo tài khoản » lên trước. */}
            <div className="mt-4 flex flex-wrap gap-2">
              {(signup ? (["register", "login"] as const) : (["login", "register"] as const)).map((k, i) => (
                <Button key={k} asChild variant={i === 0 ? "primary" : "secondary"}>
                  <Link to={withNext(`/${k}`, here, prefill)}>{k === "register" ? t("invite.createAccount") : t("invite.signIn")}</Link>
                </Button>
              ))}
            </div>
          </>
        ) : state === "working" || state === "idle" ? (
          <p>{t("invite.working")}</p>
        ) : "ws" in state ? (
          <>
            <Notice>{t("invite.done")}</Notice>
            <Button asChild className="mt-4">
              <Link to={`/w/${state.ws}/overview`}>{t("invite.open")}</Link>
            </Button>
          </>
        ) : (
          <div className="flex flex-col gap-4">
            <FormError message={state.error} />
            {state.code === "INVITATION_EMAIL_MISMATCH" ? (
              <Button className="self-start" onClick={() => void switchAccount()}>
                {t("invite.switchAccount")}
              </Button>
            ) : null}
            <PendingInvitations />
            <Link className="text-sm underline" to="/select-workspace">
              {t("invite.toWorkspaces")}
            </Link>
          </div>
        )}
      </Card>
    </main>
  );
}
